import React, { useEffect, useRef, useState } from 'react';

// Konvertierung mm zu Pixel (A4 bei 96 DPI)
const MM_TO_PX = 3.7795275591;
const mmToPx = (mm) => mm * MM_TO_PX;
const pxToMm = (px) => px / MM_TO_PX;

const BuilderCanvas = ({ elements, selectedElement, onSelectElement, onUpdateElement, canvasSettings, pageType }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [scale, setScale] = useState(0.5); // Initial scale for display
    const [draggingElement, setDraggingElement] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    const canvasWidth = mmToPx(canvasSettings.width);
    const canvasHeight = mmToPx(canvasSettings.height);

    useEffect(() => {
        drawCanvas();
    }, [elements, selectedElement, canvasSettings, pageType]);

    const drawCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');

        // Clear canvas
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        // Background
        ctx.fillStyle = canvasSettings.backgroundColor;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // Grid
        if (canvasSettings.showGrid) {
            drawGrid(ctx);
        }

        // Draw elements
        elements.forEach(element => {
            drawElement(ctx, element, element.element_id === selectedElement?.element_id);
        });
    };

    const drawGrid = (ctx) => {
        const gridSize = mmToPx(canvasSettings.gridSize);
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 0.5;

        // Vertical lines
        for (let x = 0; x <= canvasWidth; x += gridSize) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvasHeight);
            ctx.stroke();
        }

        // Horizontal lines
        for (let y = 0; y <= canvasHeight; y += gridSize) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(canvasWidth, y);
            ctx.stroke();
        }
    };

    const drawElement = (ctx, element, isSelected) => {
        const x = mmToPx(element.x_position);
        const y = mmToPx(element.y_position);
        const width = mmToPx(element.width);
        const height = mmToPx(element.height);

        ctx.save();

        // Opacity
        ctx.globalAlpha = element.opacity || 1.0;

        // Rotation
        if (element.rotation) {
            ctx.translate(x + width / 2, y + height / 2);
            ctx.rotate((element.rotation * Math.PI) / 180);
            ctx.translate(-(x + width / 2), -(y + height / 2));
        }

        switch (element.element_type) {
            case 'text':
            case 'address':
                drawTextElement(ctx, element, x, y, width, height);
                break;
            case 'rectangle':
                drawRectangleElement(ctx, element, x, y, width, height);
                break;
            case 'line':
                drawLineElement(ctx, element, x, y, width, height);
                break;
            case 'logo':
            case 'image':
                drawImageElement(ctx, element, x, y, width, height);
                break;
        }

        // Selection border
        if (isSelected) {
            ctx.strokeStyle = '#0d6efd';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.strokeRect(x, y, width, height);
            ctx.setLineDash([]);

            // Resize handles
            const handleSize = 8;
            ctx.fillStyle = '#0d6efd';
            // Top-left
            ctx.fillRect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
            // Top-right
            ctx.fillRect(x + width - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
            // Bottom-left
            ctx.fillRect(x - handleSize / 2, y + height - handleSize / 2, handleSize, handleSize);
            // Bottom-right
            ctx.fillRect(x + width - handleSize / 2, y + height - handleSize / 2, handleSize, handleSize);
        }

        ctx.restore();
    };

    const drawTextElement = (ctx, element, x, y, width, height) => {
        // Background
        if (element.background_color) {
            ctx.fillStyle = element.background_color;
            ctx.fillRect(x, y, width, height);
        }

        // Border
        if (element.border_width > 0) {
            ctx.strokeStyle = element.border_color || '#000000';
            ctx.lineWidth = element.border_width;
            ctx.strokeRect(x, y, width, height);
        }

        // Text
        ctx.fillStyle = element.font_color || '#000000';
        ctx.font = `${element.font_weight || 'normal'} ${element.font_size || 12}px ${element.font_family || 'Arial'}`;
        ctx.textAlign = element.text_align || 'left';
        ctx.textBaseline = 'top';

        const text = element.text_content || '';
        const lines = text.split('\n');
        const lineHeight = (element.font_size || 12) * 1.2;

        let textX = x;
        if (element.text_align === 'center') {
            textX = x + width / 2;
        } else if (element.text_align === 'right') {
            textX = x + width;
        }

        lines.forEach((line, index) => {
            ctx.fillText(line, textX, y + index * lineHeight);
        });
    };

    const drawRectangleElement = (ctx, element, x, y, width, height) => {
        // Background
        if (element.background_color) {
            ctx.fillStyle = element.background_color;
            ctx.fillRect(x, y, width, height);
        }

        // Border
        if (element.border_width > 0) {
            ctx.strokeStyle = element.border_color || '#000000';
            ctx.lineWidth = element.border_width;
            ctx.strokeRect(x, y, width, height);
        }
    };

    const drawLineElement = (ctx, element, x, y, width, height) => {
        ctx.strokeStyle = element.border_color || element.font_color || '#000000';
        ctx.lineWidth = element.border_width || 1;
        ctx.beginPath();
        ctx.moveTo(x, y + height / 2);
        ctx.lineTo(x + width, y + height / 2);
        ctx.stroke();
    };

    const drawImageElement = (ctx, element, x, y, width, height) => {
        // Placeholder for image
        ctx.strokeStyle = '#cccccc';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(x, y, width, height);
        ctx.setLineDash([]);

        // Icon placeholder
        ctx.fillStyle = '#999999';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(element.element_type === 'logo' ? 'Logo' : 'Bild', x + width / 2, y + height / 2);

        // TODO: Load and draw actual image if image_path exists
    };

    const handleCanvasClick = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;

        // Find clicked element (reverse order for z-index)
        const clickedElement = [...elements].reverse().find(el => {
            const elX = mmToPx(el.x_position);
            const elY = mmToPx(el.y_position);
            const elW = mmToPx(el.width);
            const elH = mmToPx(el.height);

            return x >= elX && x <= elX + elW && y >= elY && y <= elY + elH;
        });

        onSelectElement(clickedElement || null);
    };

    const handleMouseDown = (e) => {
        if (!selectedElement) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;

        const elX = mmToPx(selectedElement.x_position);
        const elY = mmToPx(selectedElement.y_position);
        const elW = mmToPx(selectedElement.width);
        const elH = mmToPx(selectedElement.height);

        if (x >= elX && x <= elX + elW && y >= elY && y <= elY + elH) {
            setDraggingElement(selectedElement);
            setDragOffset({ x: x - elX, y: y - elY });
        }
    };

    const handleMouseMove = (e) => {
        if (!draggingElement) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;

        let newX = pxToMm(x - dragOffset.x);
        let newY = pxToMm(y - dragOffset.y);

        // Snap to grid
        if (canvasSettings.snapToGrid) {
            const gridSize = canvasSettings.gridSize;
            newX = Math.round(newX / gridSize) * gridSize;
            newY = Math.round(newY / gridSize) * gridSize;
        }

        // Bounds checking
        newX = Math.max(0, Math.min(newX, canvasSettings.width - draggingElement.width));
        newY = Math.max(0, Math.min(newY, canvasSettings.height - draggingElement.height));

        onUpdateElement({
            ...draggingElement,
            x_position: newX,
            y_position: newY
        });
    };

    const handleMouseUp = () => {
        setDraggingElement(null);
    };

    return (
        <div className="builder-canvas-wrapper" ref={containerRef}>
            <canvas
                ref={canvasRef}
                width={canvasWidth}
                height={canvasHeight}
                style={{
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    cursor: draggingElement ? 'grabbing' : (selectedElement ? 'grab' : 'default')
                }}
                className="builder-canvas"
                onClick={handleCanvasClick}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            />
            <div className="mt-2 text-center">
                <small className="text-muted">
                    Zoom:
                    <button className="btn btn-sm btn-outline-secondary mx-1" onClick={() => setScale(Math.max(0.1, scale - 0.1))}>-</button>
                    {Math.round(scale * 100)}%
                    <button className="btn btn-sm btn-outline-secondary mx-1" onClick={() => setScale(Math.min(2, scale + 0.1))}>+</button>
                </small>
            </div>
        </div>
    );
};

export default BuilderCanvas;
